// /admin/v2/chapters/:chapterId/structure — Notes (by chapter), ONE page per chapter (BRIEFS
// C11.7, SPEC §10). The chapter's sections are FIXED from NCERT (structure.json, N11.8): number,
// title, NCERT page, in NCERT order — they cannot be renamed, moved or removed here. The owner
// may add his own (non-NCERT) sections anywhere; those can be renamed or switched off.
// Every row: points, last updated, status, "View" and "Upload notes for this section"
// (plain text or JSON → Check → Save). Before the chapter's first release the whole
// chapter's notes.json may be uploaded at once; after it, one section at a time only.
import React, { useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  v2Structure, v2AddSection, v2EditSection, v2SectionPoints, v2SectionDryRun, v2SectionApply,
  v2ChapterNotesDryRun, v2ChapterNotesApply, errText, fmtDateTime,
} from '../../../api/v2admin';
import V2Tabs, { Msg, chapterLabel, NOTES_CSS } from './V2Tabs';
import { Report } from './AdminV2Notes';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const unitName = (u) => [u.number, u.title].filter(Boolean).join(' ') || u.code;

// Stored html → the text format of the upload box (so a section can be edited as text).
// A point holding a table stays HTML (it may run over several lines up to </table>).
export function htmlToText(points) {
  const spans = (h) => {             // innermost first: <mark><blue>x</blue></mark> → {mark: {blue: x}}
    let out = h;
    for (let i = 0; i < 4; i += 1) {
      const next = out.replace(/<span class="c-(red|blue|green|mark)">((?:(?!<span)[\s\S])*?)<\/span>/gi, (m, c, t) => `{${c}: ${t}}`);
      if (next === out) break;
      out = next;
    }
    return out;
  };
  const conv = (h) => spans(h)          // <br> and any other allowed tag stay as they are
    .replace(/<b>([\s\S]*?)<\/b>/gi, '**$1**')
    .replace(/<i>([\s\S]*?)<\/i>/gi, '*$1*')
    .replace(/<sub>([\s\S]*?)<\/sub>/gi, '~$1~')
    .replace(/<sup>([\s\S]*?)<\/sup>/gi, '^$1^')
    .replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;|&#x27;/g, "'")
    .replace(/&nbsp;/g, ' ');
  return points.map((p) => {
    const lead = p.depth > 0 ? `${'  '.repeat(p.depth - 1)}- ` : '';
    const body = /<table/i.test(p.html) ? p.html : conv(p.html);
    return `${lead}${p.source === 'extra' ? '[extra] ' : ''}${body}`;
  }).join('\n');
}

function StatusPill({ u }) {
  const map = {
    has_notes: ['badge-green', 'Has notes'],
    empty: ['badge-yellow', 'Empty'],
    off: ['badge-red', 'Switched off'],
  };
  const [cls, label] = map[u.status] || ['badge-blue', u.status];
  return <span className={`badge ${cls}`}>{label}</span>;
}

function ExtraTag() {
  return (
    <span style={{
      marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 10,
      background: '#EEF2FF', color: '#3730A3', verticalAlign: 'middle',
    }}>Extra</span>
  );
}

function View({ code }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['v2-admin-section-points', code], queryFn: () => v2SectionPoints(code),
  });
  if (isLoading) return <div className="spinner" />;
  if (isError) return <div style={{ color: 'var(--danger)' }}>Could not load this section.</div>;
  if (!data.points.length) return <div style={{ color: 'var(--text-muted)' }}>No notes in this section yet.</div>;
  return (
    <div className="v2n" data-testid={`v2-view-${code}`}>
      {data.points.map((p) => (
        <div key={p.code} style={{ display: 'flex', gap: 10, padding: '3px 0', marginLeft: p.depth * 22 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#64748B', minWidth: 34, paddingTop: 4 }}>{p.key}</span>
          <span>
            <span dangerouslySetInnerHTML={{ __html: p.html }} />
            {p.source === 'extra' && <ExtraTag />}
          </span>
        </div>
      ))}
      {data.images.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-muted)' }}>
          Figures: {data.images.map((im) => `${im.url.split('/').pop()}${im.after ? ` (after ${im.after.split('-').pop()})` : ' (at the start)'}`).join(' · ')}
        </div>
      )}
    </div>
  );
}

const TEXT_HELP = 'One point per line. Start a line with "- " to make it a sub-point of the line above (two more spaces = one level deeper). '
  + '**bold**, {red: must memorise}, {blue: name or term}, {green: example}, {mark: exam line}, H~2~O, ^15^N. '
  + 'Start a line with [extra] for a point that is not from NCERT. A table is typed as HTML: <table><tr><th>…</th></tr><tr><td>…</td></tr></table>.';

function Upload({ unit, onDone, onClose }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [mode, setMode] = useState('text');
  const [textVal, setTextVal] = useState('');
  const [json, setJson] = useState(null);         // {name, body}
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const body = () => (mode === 'text' ? { text: textVal } : json && json.body);

  const loadCurrent = async () => {
    setMsg(null);
    try {
      const d = await v2SectionPoints(unit.code);
      setTextVal(htmlToText(d.points)); setReport(null); setLoaded(true);
    } catch (e) { setMsg({ kind: 'err', text: errText(e, 'Could not load the section.') }); }
  };

  const pick = (f) => {
    setReport(null); setJson(null); setMsg(null);
    if (!f) return;
    const rd = new FileReader();
    rd.onload = () => {
      try { setJson({ name: f.name, body: JSON.parse(String(rd.result || '')) }); }
      catch { setMsg({ kind: 'err', text: `${f.name} is not a JSON file that can be read.` }); }
    };
    rd.readAsText(f, 'utf-8');
  };

  const check = async () => {
    setBusy(true); setMsg(null);
    try { setReport(await v2SectionDryRun(unit.code, body())); }
    catch (e) { setMsg({ kind: 'err', text: errText(e, 'Could not check the notes.') }); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!window.confirm(`Save these changes into "${unitName(unit)}" now?`)) return;
    setBusy(true); setMsg(null);
    try {
      const r = await v2SectionApply(unit.code, body());
      if (r.applied) {
        qc.invalidateQueries({ queryKey: ['v2-admin-section-points', unit.code] });
        onDone(`Saved. "${unitName(unit)}" is updated. Only this section changed.`);
      } else {
        setReport(r);
        setMsg({ kind: 'err', text: r.errors?.length ? 'Nothing was saved: see the problems below.' : 'Nothing was saved: there was nothing to change.' });
      }
    } catch (e) { setMsg({ kind: 'err', text: errText(e, 'Could not save.') }); }
    finally { setBusy(false); }
  };

  const ready = mode === 'text' ? textVal.trim().length > 0 : !!json;
  const canSave = report && !report.errors?.length && report.has_changes && !busy;
  const tab = (m, label) => (
    <button type="button" className={`btn btn-sm ${mode === m ? 'btn-primary' : 'btn-ghost'}`}
      onClick={() => { setMode(m); setReport(null); setMsg(null); }}>{label}</button>
  );

  return (
    <div data-testid={`v2-upload-${unit.code}`} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 16, background: '#F8FAFC' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        <strong>Notes for {unitName(unit)}</strong>
        <span style={{ flex: 1 }} />
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 10px' }}>
        What you upload becomes the whole content of this section. Points left out are hidden (never deleted).
        First press Check: you see exactly what will change. Nothing is saved until you press Save.
      </p>
      <Msg msg={msg} />
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        {tab('text', 'Type or paste text')}
        {tab('json', 'Use a JSON file')}
      </div>
      {mode === 'text' ? (
        <div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
            {unit.points > 0 && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={loadCurrent} data-testid="v2-upload-load-current">
                Start from the notes already saved
              </button>
            )}
            {loaded && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Loaded the saved notes. Change them, then press Check.</span>}
          </div>
          <textarea value={textVal} onChange={(e) => { setTextVal(e.target.value); setReport(null); }}
            data-testid="v2-upload-text" rows={12} spellCheck={false}
            style={{ width: '100%', fontFamily: 'monospace', fontSize: 13, padding: 10, borderRadius: 8, border: '1px solid var(--border)', boxSizing: 'border-box' }}
            placeholder={'Cell wall = outer layer of a plant cell\n- made of {red: cellulose}\n[extra] Fungi: chitin'} />
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{TEXT_HELP}</div>
        </div>
      ) : (
        <div style={{ fontSize: 13 }}>
          <input ref={fileRef} type="file" accept=".json,application/json" onChange={(e) => pick(e.target.files[0])} data-testid="v2-upload-file" />
          {json && <span style={{ marginLeft: 8, color: 'var(--text-muted)' }}>{json.name}</span>}
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
            The points of this one section, as in notes.json: {'{"points": [{"key": "P01", "html": "…", "children": […]}]}'}, or one unit of a notes.json.
            Point codes of another section are refused.
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-ghost" disabled={!ready || busy} onClick={check} data-testid="v2-upload-check">1. Check</button>
        <button type="button" className="btn btn-primary" disabled={!canSave} onClick={save} data-testid="v2-upload-save">2. Save these changes</button>
      </div>
      {report && (
        <div className="card" style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
            Only <strong>{unitName(unit)}</strong> is changed. {report.counts ? plural(report.counts.points || 0, 'point', 'points') : ''} in the upload.
          </div>
          <Report r={report} section />
        </div>
      )}
    </div>
  );
}

function AddSection({ chapterId, after, onDone, onClose }) {
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    setBusy(true); setErr('');
    try {
      const r = await v2AddSection(chapterId, title, after.code);
      onDone(`Added "${r.title}" after ${unitName(after)}. It is marked Extra.`);
    } catch (e) { setErr(errText(e, 'Could not add the section.')); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="v2-add-form" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '10px 12px', background: '#EEF2FF', borderRadius: 8 }}>
      <span style={{ fontSize: 13 }}>New section after <strong>{unitName(after)}</strong>:</span>
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title of your section"
        data-testid="v2-add-title" maxLength={300}
        style={{ flex: '1 1 260px', minHeight: 40, padding: '0 10px', borderRadius: 8, border: '1px solid var(--border)' }} />
      <button type="button" className="btn btn-primary btn-sm" disabled={!title.trim() || busy} onClick={save} data-testid="v2-add-save">Add section</button>
      <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
      {err && <span style={{ color: '#B91C1C', fontSize: 13, width: '100%' }}>{err}</span>}
    </div>
  );
}

function WholeChapter({ chapterId, onDone }) {
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const pick = (f) => {
    setReport(null); setFile(null); setMsg(null);
    if (!f) return;
    const rd = new FileReader();
    rd.onload = () => {
      try { setFile({ name: f.name, json: JSON.parse(String(rd.result || '')) }); }
      catch { setMsg({ kind: 'err', text: `${f.name} is not a JSON file that can be read.` }); }
    };
    rd.readAsText(f, 'utf-8');
  };
  const check = async () => {
    setBusy(true); setMsg(null);
    try { setReport(await v2ChapterNotesDryRun(chapterId, file.json)); }
    catch (e) { setMsg({ kind: 'err', text: errText(e, 'Could not check the file.') }); }
    finally { setBusy(false); }
  };
  const save = async () => {
    if (!window.confirm('Save the whole chapter from this file now?')) return;
    setBusy(true); setMsg(null);
    try {
      const r = await v2ChapterNotesApply(chapterId, file.json);
      if (r.applied) { setReport(null); onDone('Saved. The whole chapter was loaded from the file.'); }
      else { setReport(r); setMsg({ kind: 'err', text: r.errors?.length ? 'Nothing was saved: see the problems below.' : 'Nothing was saved: there was nothing to change.' }); }
    } catch (e) { setMsg({ kind: 'err', text: errText(e, 'Could not save the file.') }); }
    finally { setBusy(false); }
  };
  const canSave = report && !report.errors?.length && report.has_changes && !busy;
  return (
    <div className="card" style={{ marginBottom: 16 }} data-testid="v2-whole-chapter">
      <div style={{ fontWeight: 700, marginBottom: 4 }}>First version: the whole chapter at once</div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 10px' }}>
        This chapter has never been released, so you may upload its whole notes.json in one go.
        Its NCERT sections must stay exactly as listed below. After the first release this box goes away
        and notes change one section at a time.
      </p>
      <Msg msg={msg} />
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="file" accept=".json,application/json" onChange={(e) => pick(e.target.files[0])} data-testid="v2-whole-file" />
        <button type="button" className="btn btn-ghost btn-sm" disabled={!file || busy} onClick={check} data-testid="v2-whole-check">1. Check the file</button>
        <button type="button" className="btn btn-primary btn-sm" disabled={!canSave} onClick={save} data-testid="v2-whole-save">2. Save these changes</button>
      </div>
      {report && <div style={{ marginTop: 12 }}><Report r={report} /></div>}
    </div>
  );
}

export default function AdminV2Structure() {
  const { chapterId } = useParams();
  const qc = useQueryClient();
  const [open, setOpen] = useState({});          // code → 'view' | 'upload'
  const [adding, setAdding] = useState(null);    // unit code
  const [renaming, setRenaming] = useState(null); // {code, title}
  const [msg, setMsg] = useState(null);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['v2-admin-structure', chapterId], queryFn: () => v2Structure(chapterId),
  });

  const units = useMemo(() => (data ? data.units : []), [data]);
  const toggle = (code, what) => setOpen((o) => ({ ...o, [code]: o[code] === what ? null : what }));
  const done = async (text) => {
    setMsg({ kind: 'ok', text }); setAdding(null); setRenaming(null);
    await refetch();
    qc.invalidateQueries({ queryKey: ['v2-admin-chapters'] });
  };
  const edit = async (u, body, okText) => {
    setMsg(null);
    try { await v2EditSection(u.code, body); await done(okText); }
    catch (e) { setMsg({ kind: 'err', text: errText(e, 'Could not change that.') }); }
  };

  if (isLoading) return <div className="spinner" />;
  if (isError || !data) {
    return (
      <div>
        <V2Tabs title="Notes (by chapter)" />
        <div className="card" style={{ color: 'var(--danger)' }}>Could not load this chapter. <Link to="/admin/v2/chapters">Back to all chapters</Link></div>
      </div>
    );
  }
  const ch = data.chapter;
  const c = data.counts;

  return (
    <div>
      <V2Tabs title={chapterLabel(ch)} />
      <div style={{ marginBottom: 12, fontSize: 14 }}><Link to="/admin/v2/chapters">← All chapters</Link></div>
      <div className="card" style={{ marginBottom: 16, padding: '16px 20px' }} data-testid="v2-structure-help">
        <div style={{ fontWeight: 700, marginBottom: 6 }}>How this page works</div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.6 }}>
          <li>This list is the chapter exactly as NCERT prints it: every section with its number, title and NCERT page. NCERT sections are fixed — they cannot be renamed, moved or removed.</li>
          <li>You can add your own section anywhere: press "Add a section after this". It is marked <strong>Extra</strong>, and you can rename it or switch it off later.</li>
          <li data-testid="v2-misc-help"><strong>Misc / Latest / Integrated</strong> (code ending in .M) is in every chapter, just before the Summary. It may stay empty. Put here things for this chapter that are not in NCERT: latest facts, links to other chapters (integrated topics), and concept or question videos. Its notes are always marked <strong>Extra</strong>. It cannot be renamed, moved or removed.</li>
          <li>Notes always go inside a section. Press "Upload notes for this section", type or paste the notes, press Check to see what will change, then Save.</li>
          <li data-testid="v2-structure-mode">
            {data.upload_mode === 'chapter'
              ? 'This chapter has never been released, so you may also upload the whole chapter at once (box below).'
              : 'This chapter has been released, so its notes change only one section at a time. Harsh\'s highlights are kept; any that need checking are shown before you save.'}
          </li>
        </ul>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
          <span className="badge badge-blue">{plural(c.ncert, 'NCERT section', 'NCERT sections')}</span>
          {c.added > 0 && <span className="badge badge-blue">{plural(c.added, 'own section', 'own sections')}</span>}
          <span className="badge badge-green">{c.with_notes} with notes</span>
          <span className="badge badge-yellow">{c.empty} empty</span>
          <span className={`badge ${ch.is_released ? 'badge-green' : 'badge-yellow'}`}>{ch.is_released ? 'Released' : 'Not released'}</span>
        </div>
      </div>
      <Msg msg={msg} />
      {data.upload_mode === 'chapter' && <WholeChapter chapterId={chapterId} onDone={done} />}
      {units.length === 0 ? (
        <div className="card">This chapter has no NCERT structure yet. It is added from its structure.json when the server starts.</div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="table" data-testid="v2-structure-table">
            <thead>
              <tr>
                <th style={{ width: 70 }}>No.</th>
                <th>Section</th>
                <th style={{ textAlign: 'right' }}>NCERT page</th>
                <th style={{ textAlign: 'right' }}>Points</th>
                <th>Last updated</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {units.map((u) => (
                <React.Fragment key={u.code}>
                  <tr data-testid={`v2-unit-${u.code}`} style={{ opacity: u.active ? 1 : 0.6 }}>
                    <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{u.number || (u.kind === 'intro' ? '—' : '')}</td>
                    <td style={{ paddingLeft: 12 + u.depth * 22 }}>
                      {renaming && renaming.code === u.code ? (
                        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          <input value={renaming.title} onChange={(e) => setRenaming({ ...renaming, title: e.target.value })}
                            data-testid="v2-rename-input" maxLength={300}
                            style={{ minHeight: 36, padding: '0 8px', borderRadius: 6, border: '1px solid var(--border)', minWidth: 220 }} />
                          <button type="button" className="btn btn-primary btn-sm" disabled={!renaming.title.trim()}
                            onClick={() => edit(u, { title: renaming.title }, 'Section renamed.')} data-testid="v2-rename-save">Save</button>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRenaming(null)}>Cancel</button>
                        </span>
                      ) : (
                        <span style={{ fontWeight: u.depth === 0 ? 600 : 400, fontStyle: u.kind === 'heading' ? 'italic' : 'normal' }}>
                          {u.title}{!u.ncert && <ExtraTag />}
                        </span>
                      )}
                      <div style={{ fontSize: 11, color: '#64748B', fontFamily: 'monospace' }}>{u.code}</div>
                    </td>
                    <td style={{ textAlign: 'right' }}>{u.page ?? '—'}</td>
                    <td style={{ textAlign: 'right' }}>{u.points}</td>
                    <td style={{ fontSize: 13, whiteSpace: 'nowrap' }}>{u.points ? fmtDateTime(u.updated_at) : '—'}</td>
                    <td><StatusPill u={u} /></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button type="button" className="btn btn-ghost btn-sm" disabled={!u.points}
                          onClick={() => toggle(u.code, 'view')} data-testid={`v2-view-btn-${u.code}`}>
                          {open[u.code] === 'view' ? 'Hide' : 'View'}
                        </button>
                        <button type="button" className="btn btn-primary btn-sm" disabled={!u.active}
                          onClick={() => toggle(u.code, 'upload')} data-testid={`v2-upload-btn-${u.code}`}>
                          Upload notes for this section
                        </button>
                        {u.kind !== 'misc' && (
                          <button type="button" className="btn btn-ghost btn-sm"
                            onClick={() => setAdding(adding === u.code ? null : u.code)} data-testid={`v2-add-btn-${u.code}`}>
                            + Add a section after this
                          </button>
                        )}
                        {u.kind === 'added_topic' && (
                          <>
                            <button type="button" className="btn btn-ghost btn-sm"
                              onClick={() => setRenaming({ code: u.code, title: u.title })}>Rename</button>
                            <button type="button" className="btn btn-ghost btn-sm" data-testid={`v2-switch-${u.code}`}
                              onClick={() => edit(u, { active: !u.active }, u.active ? 'Section switched off. Harsh no longer sees it.' : 'Section switched on again.')}>
                              {u.active ? 'Switch off' : 'Switch on'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {adding === u.code && (
                    <tr><td colSpan={7}><AddSection chapterId={chapterId} after={u} onDone={done} onClose={() => setAdding(null)} /></td></tr>
                  )}
                  {open[u.code] === 'view' && (
                    <tr><td colSpan={7} style={{ background: 'white' }}><View code={u.code} /></td></tr>
                  )}
                  {open[u.code] === 'upload' && (
                    <tr><td colSpan={7}>
                      <Upload unit={u} onClose={() => toggle(u.code, 'upload')}
                        onDone={async (t) => { setOpen((o) => ({ ...o, [u.code]: 'view' })); await done(t); }} />
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <style>{NOTES_CSS}</style>
    </div>
  );
}
